import { useTick } from '@tv2/ui-time/components/context/TimeContext';
import { Notification, NotificationButton } from '@/api/notification/useNotifiers';
import { HideUntil } from '@/hooks/behaviours/useHideable';
import { useUnknownTrack } from '@tv2/lib-offspring/hooks/tracking/useUnknownTrack';
import { useRouter } from 'next/navigation';

type UseNotificationParams = {
  notification: Notification;
  hideUntil: HideUntil;
  hideUntilSession: HideUntil;
  navigateTo: (url: string) => void;
  checkoutUrl: string;
  tracking: {
    identifier: string;
    details: Record<string, string>;
    label: string;
  };
};

export function useNotificationAction({
  notification,
  tracking,
  hideUntil,
  hideUntilSession,
  navigateTo,
  checkoutUrl,
}: UseNotificationParams): {
  dispatch: (button: NotificationButton) => Promise<void>;
} {
  const router = useRouter();
  const now = useTick();
  const unknownTrack = useUnknownTrack();
  return {
    dispatch: async (button: NotificationButton) => {
      switch (button.buttonType) {
        case 'LINK': {
          await unknownTrack({
            event: tracking.identifier,
            data: {
              header: notification.title,
              header_text: notification.title,
              body: notification.description,
              uid: notification.id,
              label: tracking.label,
              ...tracking.details,
            },
          });
          navigateTo(button.buttonLink);
          break;
        }

        case 'CONTENT': {
          await unknownTrack({
            event: tracking.identifier,
            data: {
              header: notification.title,
              header_text: notification.title,
              body: notification.description,
              uid: notification.id,
              label: tracking.label,
              ...tracking.details,
            },
          });
          // nextjs link
          router.push(button.buttonLink);
          break;
        }
        case 'CHECKOUT': {
          const url = `${checkoutUrl}/${button.buttonLink}`;
          await unknownTrack({
            event: tracking.identifier,
            data: {
              header: notification.title,
              header_text: notification.title,
              body: notification.description,
              uid: notification.id,
              label: tracking.label,
              ...tracking.details,
            },
          });
          navigateTo(url);
          break;
        }

        case 'REACTIVATE': {
          await unknownTrack({
            event: tracking.identifier,
            data: {
              header: notification.title,
              header_text: notification.title,
              body: notification.description,
              uid: notification.id,
              label: 'expirybox_reactivate',
              ...tracking.details,
            },
          });
          navigateTo(`${checkoutUrl}/genaktiver`);
          break;
        }

        case 'SNOOZE': {
          await unknownTrack({
            event: tracking.identifier,
            data: {
              label: 'expirybox_remind_me_later',
              header_text: notification.title,
              uid: notification.id,
              ...tracking.details,
            },
          });

          const snoozeDays = 3;
          const dayInSeconds = 86400;
          hideUntil(Math.round(now / 1000) + snoozeDays * dayInSeconds);
          break;
        }
        case 'CLOSE': {
          await unknownTrack({
            event: tracking.identifier,
            data: {
              label: 'promotionbannerminideck_close',
              header_text: notification.title,
              uid: notification.id,
              ...tracking.details,
            },
          });

          hideUntilSession(Math.round(now / 1000) + 10 * 86400); // hide for 10 days or until next session
          break;
        }
      }
    },
  };
}
